import { useSyncExternalStore } from "react";

// Release 1.5.0 phone mode: the bottom tab bar, big touch targets and stacked cards. On by itself on a narrow screen
// (640 px or less) and, from the account menu or the More sheet, by an explicit "Phone layout" choice kept on this device
// (not per person: it is about the screen, and it holds nothing about anyone). Off = the desktop layout, unchanged.

export const PHONE_LAYOUT_KEY = "summitops.phoneLayout";
export const PHONE_MAX_WIDTH = 640;
export const PHONE_QUERY = `(max-width: ${PHONE_MAX_WIDTH}px)`;

/** "on" / "off" = the person chose; null = automatic (narrow screen). */
export type PhoneLayoutChoice = "on" | "off" | null;

/** Phone layout or not, from the person's choice and the screen width. */
export function resolvePhoneLayout(choice: PhoneLayoutChoice, narrow: boolean): boolean {
  if (choice === "on") return true;
  if (choice === "off") return false;
  return narrow;
}

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function store(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readPhoneLayoutChoice(): PhoneLayoutChoice {
  const v = store()?.getItem(PHONE_LAYOUT_KEY);
  return v === "on" || v === "off" ? v : null;
}

export function setPhoneLayoutChoice(choice: PhoneLayoutChoice): void {
  const s = store();
  try {
    if (choice) s?.setItem(PHONE_LAYOUT_KEY, choice);
    else s?.removeItem(PHONE_LAYOUT_KEY);
  } catch {
    /* storage unavailable: the choice lasts until the tab closes */
  }
  memoryChoice = choice;
  emit();
}

let memoryChoice: PhoneLayoutChoice | undefined;

function currentChoice(): PhoneLayoutChoice {
  return memoryChoice !== undefined ? memoryChoice : readPhoneLayoutChoice();
}

function narrowNow(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && Boolean(window.matchMedia(PHONE_QUERY)?.matches);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const mql = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(PHONE_QUERY) : null;
  mql?.addEventListener?.("change", listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === PHONE_LAYOUT_KEY) {
      memoryChoice = undefined;
      listener();
    }
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    mql?.removeEventListener?.("change", listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** True when the phone layout is showing (before the 1.5.0 switch and role checks, which the caller adds). */
export function usePhoneLayoutPreferred(): boolean {
  return useSyncExternalStore(subscribe, () => resolvePhoneLayout(currentChoice(), narrowNow()), () => false);
}

/** The person's explicit choice, for the toggle's wording. */
export function usePhoneLayoutChoice(): PhoneLayoutChoice {
  return useSyncExternalStore(subscribe, currentChoice, () => null);
}

/** Flips the layout from what is showing now (the toggle in the account menu and the More sheet). */
export function togglePhoneLayout(showingPhone: boolean): void {
  const next: PhoneLayoutChoice = showingPhone ? "off" : "on";
  // Choosing what the screen would show anyway goes back to automatic.
  setPhoneLayoutChoice(resolvePhoneLayout(null, narrowNow()) === (next === "on") ? null : next);
}

/** Test helper. */
export function resetPhoneLayoutForTests(): void {
  memoryChoice = undefined;
  try {
    store()?.removeItem(PHONE_LAYOUT_KEY);
  } catch {
    /* ignore */
  }
  emit();
}
